import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PaymentsLedgerRow, PaymentsAccountRow } from '../../api/client';
import { C } from '../ChatAssistant/compact/tokens';
import { font as f } from '../ChatAssistant/compact/style';
import { Icon, Seg } from '../ChatAssistant/compact/kit';
import { accountName, describeLedger, usdCents, type TxKind } from './model';
import { pct } from './volume';
import { Head, THeadRow, Table } from './ui';
import { ROW_LINE } from './styles';
import { LoadingBlock } from '../Spinner';

const COLS = '30px minmax(180px,1.4fr) minmax(130px,1fr) 100px 110px';
type Filter = 'all' | Exclude<TxKind, 'other'>;

export function TransactionsTab({ ledger, accounts, loading }: { ledger: PaymentsLedgerRow[]; accounts: PaymentsAccountRow[]; loading: boolean }) {
  const { t } = useTranslation('paymentsCompact');
  const [filter, setFilter] = useState<Filter>('all');
  const names = new Map(accounts.map((a) => [a.account_id, accountName(a)]));
  const rows = ledger.map(describeLedger).filter((r) => filter === 'all' || r.kind === filter);

  const accountLabel = (ids: string[]) => {
    if (!ids.length) return '—';
    if (ids.length === 1) return names.get(ids[0]!) ?? ids[0]!;
    return t('tx.accounts', { count: ids.length });
  };

  return (
    <>
      <Head title={t('tx.title')} hint={t('tx.shown', { count: rows.length })}>
        <span style={{ flex: 1 }} />
        <Seg<Filter>
          value={filter}
          onChange={setFilter}
          options={(['all', 'top', 'sub', 'od', 'ref'] as const).map((k) => ({ id: k, label: t(`tx.filter.${k}`) }))}
        />
      </Head>
      <Table minWidth={640}>
        <THeadRow cols={COLS}>
          <span />
          <span>{t('tx.col.desc')}</span>
          <span>{t('tx.col.account')}</span>
          <span>{t('tx.col.date')}</span>
          <span style={{ textAlign: 'right' }}>{t('tx.col.amount')}</span>
        </THeadRow>
        {loading && !ledger.length ? (
          <LoadingBlock pad="24px 0" size={20} label={t('loading')} />
        ) : rows.length === 0 ? (
          <div style={{ padding: '24px 14px', font: f(400, 12.5), color: C.muted }}>{t('tx.empty')}</div>
        ) : rows.map((r, i) => {
          const credit = r.amountCents > 0;
          const acc = accountLabel(r.accountIds);
          return (
            <div key={r.id} style={{ display: 'grid', gridTemplateColumns: COLS, gap: 12, alignItems: 'center', height: 42, padding: '0 14px', borderTop: i ? ROW_LINE : 0 }}>
              <span style={{ width: 26, height: 26, borderRadius: 7, background: credit ? C.tint16 : 'var(--om-hover)', color: credit ? C.purpleLt : C.sub, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name={r.icon} size={13} />
              </span>
              <span style={{ font: f(500, 13), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t(`tx.desc.${r.descKey}`, r.descVars)}
                {r.volume && (
                  <span style={{ marginLeft: 8, font: f(400, 11.5), color: C.purpleLt }}>
                    {r.volume.special
                      ? t('volume.receiptSpecial', { pct: pct(r.volume.bps) })
                      : t('volume.receipt', { pct: pct(r.volume.bps), count: r.volume.count })}
                  </span>
                )}
              </span>
              <span title={r.accountIds.length > 1 ? r.accountIds.map((id) => names.get(id) ?? id).join(', ') : undefined} style={{ font: f(400, 12.5), color: C.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{acc}</span>
              <span style={{ font: f(400, 12), color: C.sub, fontVariantNumeric: 'tabular-nums' }}>{r.date}</span>
              <span style={{ textAlign: 'right', font: f(600, 13), color: credit ? C.purplePale : C.text, fontVariantNumeric: 'tabular-nums' }}>
                {credit ? '+' : '−'}{usdCents(Math.abs(r.amountCents))}
              </span>
            </div>
          );
        })}
      </Table>
    </>
  );
}
